/**
 * Unicode normalization (UAX #15) with two optional pre-passes, and an account of what changed.
 *
 * The four forms are the browser's own `String.prototype.normalize`, so they follow the
 * Unicode version the engine ships. The pre-passes are character-for-character
 * replacements that run first: full-width forms to their ASCII equivalents, and
 * ligatures to the letters they stand for. Both are things NFKC/NFKD also do, as part of
 * a much larger set of compatibility mappings; here they can be used alone, or with NFC,
 * which touches none of them.
 */

export type NormalForm = "none" | "NFC" | "NFD" | "NFKC" | "NFKD";

export const NORMAL_FORMS = ["NFC", "NFD", "NFKC", "NFKD"] as const;

export interface NormalizeOptions {
  form: NormalForm;
  /** Full-width ASCII variants, the ideographic space and the full-width currency signs. */
  fullWidth: boolean;
  /** The ff, fi, fl, ffi, ffl, st presentation forms and the Armenian ligatures. */
  ligatures: boolean;
  /** Æ, æ, Œ, œ, Ĳ and ĳ, which are letters (or digraphs) rather than presentation forms. */
  letterLigatures: boolean;
}

export const DEFAULT_NORMALIZE_OPTIONS: NormalizeOptions = {
  form: "NFC",
  fullWidth: false,
  ligatures: false,
  letterLigatures: false,
};

const FULLWIDTH_FIRST = 0xff_01;
const FULLWIDTH_LAST = 0xff_5e;
const FULLWIDTH_OFFSET = 0xfe_e0;
const IDEOGRAPHIC_SPACE = 0x30_00;
const SPACE = 0x20;

/** The full-width signs outside the ASCII block: ￠ ￡ ￢ ￣ ￤ ￥ ￦ → ¢ £ ¬ ¯ ¦ ¥ ₩. */
const FULLWIDTH_SIGNS: Readonly<Record<number, number>> = {
  65504: 0xa2,
  65505: 0xa3,
  65506: 0xac,
  65507: 0xaf,
  65508: 0xa6,
  65509: 0xa5,
  65510: 0x20_a9,
};

/** Compatibility ligatures: U+FB00–FB06 (Latin) and U+FB13–FB17 (Armenian). */
const LIGATURES: Readonly<Record<string, string>> = {
  ﬀ: "ff",
  ﬁ: "fi",
  ﬂ: "fl",
  ﬃ: "ffi",
  ﬄ: "ffl",
  ﬅ: "st",
  ﬆ: "st",
  ﬓ: "մն",
  ﬔ: "մե",
  ﬕ: "մի",
  ﬖ: "վն",
  ﬗ: "մխ",
};

const LETTER_LIGATURES: Readonly<Record<string, string>> = {
  Æ: "AE",
  æ: "ae",
  Œ: "OE",
  œ: "oe",
  Ĳ: "IJ",
  ĳ: "ij",
};

function widthOf(code: number): string | null {
  if (code >= FULLWIDTH_FIRST && code <= FULLWIDTH_LAST) {
    return String.fromCodePoint(code - FULLWIDTH_OFFSET);
  }
  if (code === IDEOGRAPHIC_SPACE) {
    return String.fromCodePoint(SPACE);
  }
  const sign = FULLWIDTH_SIGNS[code];
  return sign === undefined ? null : String.fromCodePoint(sign);
}

function mapCharacter(char: string, options: NormalizeOptions): string {
  const code = char.codePointAt(0) ?? 0;
  if (options.fullWidth) {
    const narrow = widthOf(code);
    if (narrow !== null) {
      return narrow;
    }
  }
  if (options.ligatures && char in LIGATURES) {
    return LIGATURES[char] ?? char;
  }
  if (options.letterLigatures && char in LETTER_LIGATURES) {
    return LETTER_LIGATURES[char] ?? char;
  }
  return char;
}

/** Apply the replacement passes only; no normalization form. */
export function mapCharacters(text: string, options: NormalizeOptions): string {
  if (!(options.fullWidth || options.ligatures || options.letterLigatures)) {
    return text;
  }
  let out = "";
  for (const char of text) {
    out += mapCharacter(char, options);
  }
  return out;
}

export function transformText(text: string, options: NormalizeOptions): string {
  const mapped = mapCharacters(text, options);
  return options.form === "none" ? mapped : mapped.normalize(options.form);
}

// ---------------------------------------------------------------------------
// Measuring

const SURROGATE_FIRST = 0xd8_00;
const SURROGATE_LAST = 0xdf_ff;
const UTF8_ONE = 0x7f;
const UTF8_TWO = 0x7_ff;
const UTF8_THREE = 0xff_ff;

export function utf8Length(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    if (code <= UTF8_ONE) {
      bytes += 1;
    } else if (code <= UTF8_TWO) {
      bytes += 2;
    } else if (code <= UTF8_THREE) {
      // A lone surrogate is written as U+FFFD, which is three bytes too.
      bytes += 3;
    } else {
      bytes += 4;
    }
  }
  return bytes;
}

export function codePointCount(text: string): number {
  let count = 0;
  for (const _char of text) {
    count += 1;
  }
  return count;
}

export interface Measure {
  codePoints: number;
  utf16Units: number;
  utf8Bytes: number;
}

export function measure(text: string): Measure {
  return {
    codePoints: codePointCount(text),
    utf16Units: text.length,
    utf8Bytes: utf8Length(text),
  };
}

/** `U+0065 U+0301` for the code points of a string. */
export function codePointList(text: string): string {
  return [...text]
    .map((char) => {
      const code = char.codePointAt(0) ?? 0;
      return `U+${code.toString(16).toUpperCase().padStart(4, "0")}`;
    })
    .join(" ");
}

export function isLoneSurrogate(char: string): boolean {
  const code = char.codePointAt(0) ?? 0;
  return code >= SURROGATE_FIRST && code <= SURROGATE_LAST;
}

export type FormReport = Record<(typeof NORMAL_FORMS)[number], boolean>;

/** Which forms the text is already in. */
export function detectForms(text: string): FormReport {
  return {
    NFC: text.normalize("NFC") === text,
    NFD: text.normalize("NFD") === text,
    NFKC: text.normalize("NFKC") === text,
    NFKD: text.normalize("NFKD") === text,
  };
}

// ---------------------------------------------------------------------------
// What changed

const RE_MARK = /^\p{M}$/u;
const ASCII_LAST = 0x7f;

/**
 * Cut the text into pieces that can be transformed one at a time and joined again to
 * give the same answer as transforming the whole text. A piece is a base character with
 * the combining marks after it, plus any following character the transformation
 * changes when it meets the piece (a Hangul vowel after a consonant, for instance).
 */
export function chunkText(text: string, options: NormalizeOptions): string[] {
  const chunks: string[] = [];
  let current = "";
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    const attaches =
      current !== "" &&
      code > ASCII_LAST &&
      (RE_MARK.test(char) || interacts(current, char, options));
    if (attaches) {
      current += char;
    } else {
      if (current !== "") {
        chunks.push(current);
      }
      current = char;
    }
  }
  if (current !== "") {
    chunks.push(current);
  }
  return chunks;
}

function interacts(
  current: string,
  char: string,
  options: NormalizeOptions
): boolean {
  if (options.form === "none") {
    return false;
  }
  return (
    transformText(current + char, options) !==
    transformText(current, options) + transformText(char, options)
  );
}

export interface ChangeRow {
  before: string;
  after: string;
  count: number;
  /** UTF-8 bytes added (positive) or saved (negative) per occurrence. */
  byteDelta: number;
}

export interface ChangeReport {
  rows: ChangeRow[];
  /** Pieces of the text that changed, counting repeats. */
  changedPieces: number;
}

export function describeChanges(
  text: string,
  options: NormalizeOptions
): ChangeReport {
  const whole = transformText(text, options);
  const pieces = chunkText(text, options);
  const rebuilt = pieces.map((piece) => transformText(piece, options)).join("");
  const byKey = new Map<string, ChangeRow>();
  let changedPieces = 0;
  if (rebuilt !== whole) {
    // Pieces did not reproduce the whole: report the text as a single change.
    return whole === text
      ? { rows: [], changedPieces: 0 }
      : {
          rows: [
            {
              before: text,
              after: whole,
              count: 1,
              byteDelta: utf8Length(whole) - utf8Length(text),
            },
          ],
          changedPieces: 1,
        };
  }
  for (const piece of pieces) {
    const after = transformText(piece, options);
    if (after === piece) {
      continue;
    }
    changedPieces += 1;
    const key = `${piece}\u0000${after}`;
    const row = byKey.get(key);
    if (row) {
      row.count += 1;
    } else {
      byKey.set(key, {
        before: piece,
        after,
        count: 1,
        byteDelta: utf8Length(after) - utf8Length(piece),
      });
    }
  }
  return { rows: [...byKey.values()], changedPieces };
}
